import { useQuery } from '@tanstack/react-query';
import {
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  Search,
  Table as TableIcon,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import { BASE as SERVICES_BASE, listCategories } from '../api';
import { SERVICE_UNITS, serviceUnitLabel } from '../constants';
import { serviceKeys, useDeleteService, useServices } from '../queries';
import { useServicesUi, type ServicesViewMode } from '../store/viewPrefs';
import type { Service } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type ServiceOrderingColumn = 'name' | 'unit' | 'base_rate' | 'is_active';

const VIEW_MODES: readonly ViewModeOption<ServicesViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by category', icon: Layers },
];

const ACTIVE_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
] as const;

function formatRate(value: string): string {
  return formatMoney(value, 'USD') ?? '—';
}

export function ServicesListPage() {
  const navigate = useNavigate();
  const view = useServicesUi((s) => s.view);
  const setView = useServicesUi((s) => s.setView);
  const ordering = useServicesUi((s) => s.ordering);
  const setOrdering = useServicesUi((s) => s.setOrdering);

  // Server-driven filters live in local state — pagination + search hit the API.
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [active, setActive] = useState('all');
  const [category, setCategory] = useState('all');
  const [unit, setUnit] = useState('all');
  const [page, setPage] = useState(1);

  // Category names for the filter Select and the grouped view header labels.
  const categoriesQuery = useQuery({
    queryKey: [...serviceKeys.all, 'categories'],
    queryFn: () => listCategories(),
  });
  const categories = categoriesQuery.data?.results ?? [];
  const categoryName = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of categoriesQuery.data?.results ?? []) map.set(c.id, c.name);
    return map;
  }, [categoriesQuery.data]);

  const { data, isPending, isError, error, isFetching } = useServices({
    page,
    search: search || undefined,
    is_active: active === 'all' ? undefined : active === 'true',
    category: category === 'all' ? undefined : Number(category),
    unit: unit === 'all' ? undefined : unit,
    ordering,
  });

  const results = data?.results ?? [];
  const open = (s: Service) => navigate(`/finance/catalog/${s.public_id}`);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Service Catalog</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Org-wide rate card — services and their base rates. Proposals and invoices snapshot these
            at the time of use.
          </p>
        </div>
        <Button onClick={() => navigate('/finance/catalog/new')}>
          <Plus /> New service
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by name or description…"
              className="pl-8"
              aria-label="Search services"
            />
          </div>
          <Select
            value={category}
            onValueChange={(v) => {
              setPage(1);
              setCategory(v);
            }}
          >
            <SelectTrigger className="w-44" aria-label="Filter by category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={unit}
            onValueChange={(v) => {
              setPage(1);
              setUnit(v);
            }}
          >
            <SelectTrigger className="w-40" aria-label="Filter by unit">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All units</SelectItem>
              {SERVICE_UNITS.map((u) => (
                <SelectItem key={u.value} value={u.value}>
                  {u.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={active}
            onValueChange={(v) => {
              setPage(1);
              setActive(v);
            }}
          >
            <SelectTrigger className="w-40" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIVE_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `services.csv` without leaving the SPA.
              void exportCsv(SERVICES_BASE, {
                search: search || undefined,
                is_active: active === 'all' ? undefined : active,
                category: category === 'all' ? undefined : category,
                unit: unit === 'all' ? undefined : unit,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export services as CSV"
            title="Export services as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <ServicesSkeleton />
      ) : results.length === 0 ? (
        <ServicesTable
          services={[]}
          onRowClick={() => undefined}
          categoryName={categoryName}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <ServicesTable
          services={results}
          onRowClick={open}
          categoryName={categoryName}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <ServicesList services={results} onOpen={open} categoryName={categoryName} />
      ) : view === 'cards' ? (
        <ServicesCards services={results} onOpen={open} />
      ) : (
        <ServicesGroupedByCategory services={results} onOpen={open} categoryName={categoryName} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'service' : 'services'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  services: Service[];
  onOpen: (s: Service) => void;
  categoryName: Map<number, string>;
}

interface ServicesTableProps {
  services: Service[];
  onRowClick: (s: Service) => void;
  categoryName: Map<number, string>;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function categoryLabel(s: Service, categoryName: Map<number, string>): string {
  if (s.category == null) return 'Uncategorized';
  return categoryName.get(s.category) ?? '…';
}

function ServicesTable({
  services,
  onRowClick,
  categoryName,
  ordering,
  onOrderingChange,
}: ServicesTableProps) {
  const del = useDeleteService();
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<ServiceOrderingColumn>
              column="name"
              value={ordering}
              onChange={onOrderingChange}
            >
              Name
            </SortableTableHead>
            <TableHead className="hidden sm:table-cell">Category</TableHead>
            <SortableTableHead<ServiceOrderingColumn>
              column="unit"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Unit
            </SortableTableHead>
            <SortableTableHead<ServiceOrderingColumn>
              column="base_rate"
              value={ordering}
              onChange={onOrderingChange}
            >
              Base rate
            </SortableTableHead>
            <SortableTableHead<ServiceOrderingColumn>
              column="is_active"
              value={ordering}
              onChange={onOrderingChange}
            >
              Active
            </SortableTableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {services.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-muted-foreground py-10 text-center">
                No services found.
              </TableCell>
            </TableRow>
          ) : (
            services.map((s) => (
              <TableRow key={s.id} className="cursor-pointer" onClick={() => onRowClick(s)}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {categoryLabel(s, categoryName)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {serviceUnitLabel(s.unit)}
                </TableCell>
                <TableCell className="tabular-nums">{formatRate(s.base_rate)}</TableCell>
                <TableCell>
                  <Badge variant={s.is_active ? 'default' : 'secondary'}>
                    {s.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Delete ${s.name}`}
                        title="Delete"
                        disabled={del.isPending}
                      >
                        <Trash2 className="text-destructive size-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete “{s.name}”?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Removes the service from the rate card. Issued proposals and invoices
                          keep their snapshotted lines.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del.mutate(s.public_id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ServicesCards({ services, onOpen }: Omit<ListProps, 'categoryName'>) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {services.map((s) => (
        <Card
          key={s.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(s)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(s);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{s.name}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={s.is_active ? 'default' : 'secondary'}>
              {s.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <span className="text-muted-foreground truncate">{serviceUnitLabel(s.unit)}</span>
            <span className="text-muted-foreground ml-auto tabular-nums">
              {formatRate(s.base_rate)}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ServicesList({ services, onOpen, categoryName }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {services.map((s) => (
        <li key={s.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(s)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">{s.name}</span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {categoryLabel(s, categoryName)}
            </span>
            <Badge variant={s.is_active ? 'default' : 'secondary'} className="shrink-0">
              {s.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <span className="text-muted-foreground text-xs tabular-nums">
              {formatRate(s.base_rate)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ServicesGroupedByCategory({ services, onOpen, categoryName }: ListProps) {
  // Bucket by category name, falling back to active/inactive when a service has
  // no category. Empty groups are skipped.
  const groups = useMemo(() => {
    const buckets = new Map<string, Service[]>();
    for (const s of services) {
      const key = categoryLabel(s, categoryName);
      const bucket = buckets.get(key);
      if (bucket) bucket.push(s);
      else buckets.set(key, [s]);
    }
    return [...buckets.entries()]
      .map(([label, bucket]) => ({ label, bucket }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [services, categoryName]);

  return (
    <div className="space-y-6">
      {groups.map(({ label, bucket }) => (
        <section key={label} aria-labelledby={`services-category-${label}`}>
          <h2
            id={`services-category-${label}`}
            className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
          >
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <ServicesCards services={bucket} onOpen={onOpen} />
        </section>
      ))}
    </div>
  );
}

function ServicesSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
